import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { ProfileAttribute, ProfileAttributeDialog } from '~/components/screens/teams/components/dialogs/ProfileAttributeDialog';
import { CREATE_PROFILE_ATTRIBUTE, UPDATE_PROFILE_ATTRIBUTE, DELETE_PROFILE_ATTRIBUTE, GET_PROFILE_ATTRIBUTES } from '~/services/ProfileAttributes';
import EditIcon from '~/assets/icons/EditIcon';
import DeleteIcon from '~/assets/icons/DeleteIcon';

interface ProfileAttributesProps {
  searchValue: string;
  selectedFilter: string;
  selectedSort: string;
  isAddClicked: boolean;
  setIsAddClicked: (v: boolean) => void;
}

export default function ProfileAttributes({
  searchValue,
  selectedFilter,
  selectedSort,
  isAddClicked,
  setIsAddClicked,
}: ProfileAttributesProps) {
  const [t] = useTranslation('teams');
  const [openDialog, setOpenDialog] = useState(false);
  const [editTarget, setEditTarget] = useState<ProfileAttribute | null>(null);
  const { agentId } = useParams();

  const { data, loading, refetch } = useQuery(GET_PROFILE_ATTRIBUTES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId,
  });

  const [createProfileAttribute] = useMutation(CREATE_PROFILE_ATTRIBUTE);
  const [updateProfileAttribute] = useMutation(UPDATE_PROFILE_ATTRIBUTE);
  const [deleteProfileAttribute] = useMutation(DELETE_PROFILE_ATTRIBUTE);

  const attributes = data?.get_profile_attributes?.data || [];

  useEffect(() => {
    if (isAddClicked) {
      setEditTarget(null);
      setOpenDialog(true);
      setIsAddClicked(false);
    }
  }, [isAddClicked, setIsAddClicked]);

  const filtered = useMemo(() => {
    const filterKey = selectedFilter || 'name';
    const base = attributes.filter((v: any) => {
      const value = (v as any)[filterKey];
      return (value ?? '').toString().toLowerCase().includes(searchValue.toLowerCase());
    });

    const sortKey = selectedSort || 'name';
    return [...base].sort((a: any, b: any) => {
      const av = (a[sortKey] ?? '').toString().toLowerCase();
      const bv = (b[sortKey] ?? '').toString().toLowerCase();
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
  }, [attributes, selectedFilter, selectedSort, searchValue]);

  const close = () => setOpenDialog(false);

  const upsertAttribute = async (value: ProfileAttribute) => {
    try {
      if (value.id) {
        // Find the original attribute to get the _id
        const originalAttribute = attributes.find((attr: any) => attr._id === value.id || attr.id === value.id);
        const attributeId = originalAttribute?._id || value.id;

        // Clean closelist object to remove _id field
        const cleanCloselist = value.dataType === 'closelist' && value.closelist ? {
          options: value.closelist.options,
          multiple: value.closelist.multiple,
        } : undefined;

        // Update existing attribute
        await updateProfileAttribute({
          variables: {
            data: {
              agent_id: agentId,
              attribute_id: attributeId,
              name: value.name,
              dataType: value.dataType,
              description: value.description,
              useAI: value.useAI,
              closelist: cleanCloselist,
            },
          },
        });
      } else {
        // Clean closelist object to remove _id field for create
        const cleanCloselist = value.dataType === 'closelist' && value.closelist ? {
          options: value.closelist.options,
          multiple: value.closelist.multiple,
        } : undefined;

        // Create new attribute
        await createProfileAttribute({
          variables: {
            data: {
              agent_id: agentId,
              name: value.name,
              dataType: value.dataType,
              description: value.description,
              useAI: value.useAI,
              closelist: cleanCloselist,
            },
          },
        });
      }
      // Refetch the data to update the list
      refetch();
      setEditTarget(null);
      setOpenDialog(false);
    } catch (error) {
      console.error('Error upserting profile attribute:', error);
      // TODO: Add error handling/notification
    }
  };

  const onEdit = (v: any) => {
    // Map _id to id for the dialog
    const editData = {
      ...v,
      id: v._id || v.id,
    };
    setEditTarget(editData);
    setOpenDialog(true);
  };

  const onDelete = async (id?: string) => {
    if (!id) return;
    
    try {
      await deleteProfileAttribute({
        variables: {
          data: {
            agent_id: agentId,
            attribute_id: id,
          },
        },
      });
      // Refetch the data to update the list
      refetch();
    } catch (error) {
      console.error('Error deleting profile attribute:', error);
      // TODO: Add error handling/notification
    }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100">
        <table className="table">
          <thead>
            <tr>
              <th>{t('label_name')}</th>
              <th>{t('field_data_type')}</th>
              <th>{t('field_description')}</th>
              <th>{t('field_use_ai')}</th>
              <th>{t('options')}</th>
              <th className="text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-base-content/60">
                  {t('loading')}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-base-content/60">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-base-content/60">📋</span>
                    <span className="text-base-content/60 font-medium">
                      {t('no_data')}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((v: any) => (
                <tr key={v.id || v._id}>
                  <td className="font-medium">{v.name}</td>
                  <td className="capitalize">{v.dataType}</td>
                  <td className="max-w-[420px] truncate" title={v.description}>{v.description}</td>
                  <td>
                    {v.useAI ? (
                      <span className="badge badge-success badge-outline">AI</span>
                    ) : (
                      <span className="badge">—</span>
                    )}
                  </td>
                  <td className="max-w-[200px]">
                    {v.dataType === 'openlist' && (
                      <span className="badge">—</span>
                    )}
                    {v.dataType === 'closelist' && v.closelist ? (
                      <div className="space-y-1">
                        <div className="flex flex-wrap gap-1">
                          {v.closelist.options.slice(0, 3).map((option: string, index: number) => (
                            <span key={index} className="badge badge-sm badge-outline">
                              {option}
                            </span>
                          ))}
                          {v.closelist.options.length > 3 && (
                            <span className="badge badge-sm">+{v.closelist.options.length - 3}</span>
                          )}
                        </div>
                        {v.closelist.multiple && (
                          <span className="text-xs text-base-content/60">
                            {t('multiple_selection')}
                          </span>
                        )}
                      </div>
                    ) : v.dataType !== 'openlist' ? (
                      <span className="text-base-content/60">—</span>
                    ) : null}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        className="btn btn-sm btn-ghost hover:btn-primary" 
                        onClick={() => onEdit(v)}
                        title={t('edit')}
                      >
                        <EditIcon />
                      </button>
                      <button 
                        className="btn btn-sm btn-ghost hover:btn-error" 
                        onClick={() => onDelete(v.id || v._id)}
                        title={t('delete')}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ProfileAttributeDialog
        isOpen={openDialog}
        isEdit={!!editTarget}
        initialValue={editTarget}
        onClose={() => {
          setEditTarget(null);
          close();
        }}
        onSubmit={upsertAttribute}
      />
    </div>
  );
}
